import { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { CheckCircle2, Loader2, Phone } from "lucide-react";
import { toast } from "sonner";
import { TIPOLOGIE, PHONE_TEL, PHONE_DISPLAY } from "@/data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initial = {
  nome: "",
  telefono: "",
  email: "",
  comune: "",
  tipologia: "",
  messaggio: "",
  privacy: false,
  azienda: "",
};

const inputCls =
  "w-full bg-panna border border-anthracite/20 px-4 py-3.5 text-sm text-anthracite placeholder:text-concrete/70 outline-none transition-[border-color,box-shadow] duration-300 focus:border-terracotta focus:ring-1 focus:ring-terracotta";

const ContactForm = () => {
  const [form, setForm] = useState(initial);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const set = (k) => (e) =>
    setForm((f) => ({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.privacy) {
      toast.error("Per inviare la richiesta è necessario accettare la privacy policy.");
      return;
    }
    setSending(true);
    try {
      const res = await fetch(`${API}/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.detail || "Errore durante l'invio");
      }
      setSent(true);
    } catch (err) {
      toast.error(
        `Non è stato possibile inviare la richiesta. Puoi chiamarci al ${PHONE_DISPLAY} o scriverci su WhatsApp.`
      );
    } finally {
      setSending(false);
    }
  };

  return (
    <div data-testid="contact-form-wrapper" className="relative">
      <AnimatePresence mode="wait">
        {sent ? (
          <motion.div
            key="ok"
            data-testid="contact-success"
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
            className="bg-panna border border-terracotta/30 p-10 md:p-14 text-center"
          >
            <CheckCircle2 size={44} strokeWidth={1.25} className="text-terracotta mx-auto mb-6" />
            <h3 className="font-display text-2xl md:text-3xl tracking-tight text-anthracite">
              Richiesta inviata.
            </h3>
            <p className="mt-4 text-concrete leading-relaxed max-w-md mx-auto">
              Grazie per averci contattato. Ti ricontattiamo il prima possibile per
              organizzare il sopralluogo. Se preferisci, puoi chiamarci subito.
            </p>
            <a
              href={PHONE_TEL}
              data-testid="success-call-button"
              className="mt-8 inline-flex items-center gap-2 bg-terracotta hover:bg-terracotta-hover text-ivory font-display text-sm tracking-[0.12em] uppercase px-8 py-4 transition-colors duration-300"
            >
              <Phone size={16} strokeWidth={1.5} /> Chiama ora — {PHONE_DISPLAY}
            </a>
          </motion.div>
        ) : (
          <motion.form
            key="form"
            data-testid="contact-form"
            onSubmit={submit}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0, y: -16 }}
            className="grid grid-cols-1 md:grid-cols-2 gap-5"
            noValidate={false}
          >
            <div className="hidden" aria-hidden="true">
              <label>
                Azienda
                <input
                  type="text"
                  name="azienda"
                  tabIndex={-1}
                  autoComplete="off"
                  value={form.azienda}
                  onChange={set("azienda")}
                />
              </label>
            </div>

            <div>
              <label htmlFor="cf-nome" className="block font-display text-xs tracking-[0.2em] uppercase text-concrete mb-2">
                Nome e cognome *
              </label>
              <input id="cf-nome" data-testid="contact-input-nome" required minLength={2} className={inputCls} value={form.nome} onChange={set("nome")} placeholder="Mario Rossi" />
            </div>
            <div>
              <label htmlFor="cf-telefono" className="block font-display text-xs tracking-[0.2em] uppercase text-concrete mb-2">
                Telefono *
              </label>
              <input id="cf-telefono" data-testid="contact-input-telefono" required minLength={6} type="tel" className={inputCls} value={form.telefono} onChange={set("telefono")} placeholder="333 000 0000" />
            </div>
            <div>
              <label htmlFor="cf-email" className="block font-display text-xs tracking-[0.2em] uppercase text-concrete mb-2">
                E-mail *
              </label>
              <input id="cf-email" data-testid="contact-input-email" required type="email" className={inputCls} value={form.email} onChange={set("email")} placeholder="mario.rossi@email.it" />
            </div>
            <div>
              <label htmlFor="cf-comune" className="block font-display text-xs tracking-[0.2em] uppercase text-concrete mb-2">
                Comune dell'immobile *
              </label>
              <input id="cf-comune" data-testid="contact-input-comune" required minLength={2} className={inputCls} value={form.comune} onChange={set("comune")} placeholder="Nibionno, Lecco, Oggiono…" />
            </div>
            <div className="md:col-span-2">
              <label htmlFor="cf-tipologia" className="block font-display text-xs tracking-[0.2em] uppercase text-concrete mb-2">
                Tipologia di intervento *
              </label>
              <select id="cf-tipologia" data-testid="contact-select-tipologia" required className={inputCls} value={form.tipologia} onChange={set("tipologia")}>
                <option value="" disabled>Seleziona il tipo di lavoro</option>
                {TIPOLOGIE.map((t) => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>
            </div>
            <div className="md:col-span-2">
              <label htmlFor="cf-messaggio" className="block font-display text-xs tracking-[0.2em] uppercase text-concrete mb-2">
                Messaggio *
              </label>
              <textarea
                id="cf-messaggio"
                data-testid="contact-input-messaggio"
                required
                minLength={5}
                rows={5}
                className={`${inputCls} resize-y`}
                value={form.messaggio}
                onChange={set("messaggio")}
                placeholder="Raccontaci cosa vuoi realizzare: tipo di immobile, lavori che hai in mente, tempistiche…"
              />
            </div>
            <div className="md:col-span-2">
              <label className="flex items-start gap-3 cursor-pointer text-sm text-concrete leading-relaxed">
                <input
                  type="checkbox"
                  data-testid="contact-privacy-checkbox"
                  checked={form.privacy}
                  onChange={set("privacy")}
                  required
                  className="mt-1 w-4 h-4 accent-[#C05A33]"
                />
                <span>
                  Ho letto la{" "}
                  <Link to="/privacy-policy" data-testid="contact-privacy-link" className="text-terracotta underline underline-offset-2">
                    Privacy Policy
                  </Link>{" "}
                  e acconsento al trattamento dei miei dati per essere ricontattato. *
                </span>
              </label>
            </div>
            <div className="md:col-span-2">
              <button
                type="submit"
                data-testid="contact-submit-button"
                disabled={sending}
                className="w-full md:w-auto inline-flex items-center justify-center gap-3 bg-terracotta hover:bg-terracotta-hover disabled:opacity-60 text-ivory font-display text-sm tracking-[0.15em] uppercase px-10 py-4 transition-colors duration-300"
              >
                {sending && <Loader2 size={16} className="animate-spin" />}
                {sending ? "Invio in corso…" : "Richiedi un sopralluogo"}
              </button>
            </div>
          </motion.form>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ContactForm;
